/**
 * Structured logging for the web application
 * Aligns with the API's observability patterns from apps/api/internal/observability
 *
 * Automatically includes correlation IDs from Google Analytics for distributed tracing
 */

import { getBuildInfo } from "../build-info";

// Lazy import to avoid circular dependencies
let getCorrelationIdFromGA: (() => string) | null = null;

/**
 * Get correlation ID from GA4 session (lazy loaded to avoid circular deps)
 */
function getSessionCorrelationId(): string | undefined {
  if (!getCorrelationIdFromGA) {
    try {
      const ga4 = require("../analytics/ga4");
      getCorrelationIdFromGA = ga4.getSessionCorrelationId;
    } catch {
      // GA4 not available (SSR or not initialized)
      return undefined;
    }
  }

  try {
    return getCorrelationIdFromGA?.();
  } catch {
    return undefined;
  }
}

export type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

/**
 * GCP Cloud Logging severity mapping
 * https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#LogSeverity
 */
function gcpSeverity(level: LogLevel): string {
  switch (level) {
    case "debug":
      return "DEBUG";
    case "info":
      return "INFO";
    case "warn":
      return "WARNING";
    case "error":
      return "ERROR";
    case "fatal":
      return "CRITICAL";
  }
}

/**
 * Parse log level from environment with sensible defaults
 * - Production: WARN (suppress INFO logs in production)
 * - All other envs: INFO (show INFO locally/dev/staging)
 */
function parseLogLevel(): LogLevel {
  const logLevel = process.env.NEXT_PUBLIC_LOG_LEVEL || process.env.LOG_LEVEL;
  if (logLevel) {
    return logLevel as LogLevel;
  }

  const env = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV;
  return env === "production" || env === "prod" ? "warn" : "info";
}

const MIN_LOG_LEVEL = parseLogLevel();

/**
 * Check if a log level should be logged based on minimum level
 */
function shouldLog(level: LogLevel): boolean {
  const levels: LogLevel[] = ["debug", "info", "warn", "error", "fatal"];
  const currentIndex = levels.indexOf(level);
  const minIndex = levels.indexOf(MIN_LOG_LEVEL);
  return currentIndex >= minIndex;
}

/**
 * Field for structured logging
 */
export interface Field {
  key: string;
  value: unknown;
}

/**
 * Context that can be attached to log entries
 */
export interface LogContext {
  [key: string]: unknown;
}

/**
 * HTTP request information for Cloud Logging
 * https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#HttpRequest
 */
export interface HttpRequestInfo {
  requestMethod?: string;
  requestUrl?: string;
  requestSize?: string;
  status?: number;
  responseSize?: string;
  userAgent?: string;
  remoteIp?: string;
  serverIp?: string;
  referer?: string;
  latency?: string;
  cacheLookup?: boolean;
  cacheHit?: boolean;
  cacheValidatedWithOriginServer?: boolean;
  cacheFillBytes?: string;
  protocol?: string;
}

/**
 * Structured log entry for Google Cloud Logging
 * https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry
 */
export interface LogEntry {
  // GCP standard fields
  severity: string; // DEBUG, INFO, WARNING, ERROR, CRITICAL
  message: string;
  timestamp: string; // ISO 8601 UTC

  // Trace correlation (format: projects/PROJECT_ID/traces/TRACE_ID)
  "logging.googleapis.com/trace"?: string;
  "logging.googleapis.com/spanId"?: string;

  // Labels for filtering/partitioning in BigQuery
  "logging.googleapis.com/labels"?: {
    service?: string;
    component?: string;
    environment?: string;
    version?: string;
  };

  // HTTP request info (auto-recognized by Cloud Logging)
  httpRequest?: HttpRequestInfo;

  // Source location (auto-recognized by Cloud Logging)
  "logging.googleapis.com/sourceLocation"?: {
    file?: string;
    line?: string;
    function?: string;
  };

  // Custom fields (end up in jsonPayload in Cloud Logging)
  level?: LogLevel; // Keep for compatibility
  service?: string;
  component?: string;
  environment?: string;
  version?: string;
  correlation_id?: string;

  error?: {
    name: string;
    message: string;
    stack?: string;
  };

  [key: string]: unknown;
}

/**
 * Get GCP project ID for trace formatting
 */
function getProjectID(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_GCP_PROJECT_ID ||
    process.env.GCP_PROJECT_ID ||
    process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID
  );
}

/**
 * Create a structured log entry with GCP Cloud Logging compatibility
 */
function createLogEntry(
  service: string,
  component: string | undefined,
  level: LogLevel,
  message: string,
  fields: Field[] = [],
): LogEntry {
  const buildInfo = getBuildInfo();
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV;

  const entry: LogEntry = {
    // GCP standard fields (these are recognized by Cloud Logging)
    severity: gcpSeverity(level),
    message,
    timestamp: new Date().toISOString(),

    // Backwards compatibility
    level,
    service,
    component,
  };

  // Add labels for BigQuery partitioning and filtering
  // These are extracted by Cloud Logging and can be used for efficient queries
  const labels: Record<string, string> = {};
  if (service) labels.service = service;
  if (component) labels.component = component;
  if (env) labels.environment = env;
  if (buildInfo.version) labels.version = buildInfo.version;

  entry["logging.googleapis.com/labels"] = labels;

  // Also add to main payload for compatibility
  if (env) {
    entry.environment = env;
  }
  if (buildInfo.version) {
    entry.version = buildInfo.version;
  }

  // Automatically include correlation ID from GA4 session (if available)
  // This enables tracing frontend logs to backend API calls
  const sessionCorrelationId = getSessionCorrelationId();
  if (sessionCorrelationId) {
    entry.correlation_id = sessionCorrelationId;
    const projectID = getProjectID();
    if (projectID) {
      entry["logging.googleapis.com/trace"] =
        `projects/${projectID}/traces/${sessionCorrelationId}`;
    }
  }

  // Add custom fields from arguments (these can override auto-detected correlation ID)
  for (const field of fields) {
    // Special handling for trace/correlation IDs
    if (field.key === "trace_id" && typeof field.value === "string") {
      const projectID = getProjectID();
      if (projectID) {
        // Format trace for GCP: projects/PROJECT_ID/traces/TRACE_ID
        entry["logging.googleapis.com/trace"] =
          `projects/${projectID}/traces/${field.value}`;
      }
      entry.correlation_id = field.value;
    } else if (field.key === "span_id" && typeof field.value === "string") {
      entry["logging.googleapis.com/spanId"] = field.value;
    } else if (
      field.key === "correlation_id" &&
      typeof field.value === "string"
    ) {
      entry.correlation_id = field.value;
      // Also use as trace if no explicit trace_id
      if (!entry["logging.googleapis.com/trace"]) {
        const projectID = getProjectID();
        if (projectID) {
          entry["logging.googleapis.com/trace"] =
            `projects/${projectID}/traces/${field.value}`;
        }
      }
    } else if (field.key === "httpRequest" || field.key === "http_request") {
      // HTTP request info is auto-recognized by Cloud Logging
      entry.httpRequest = field.value as HttpRequestInfo;
    } else {
      // All other fields go into jsonPayload
      entry[field.key] = field.value;
    }
  }

  return entry;
}

/**
 * Output log entry to console with appropriate method
 */
function outputLog(entry: LogEntry): void {
  const output = JSON.stringify(entry);

  switch (entry.level) {
    case "debug":
      console.debug(output);
      break;
    case "info":
      console.info(output);
      break;
    case "warn":
      console.warn(output);
      break;
    case "error":
    case "fatal":
      console.error(output);
      break;
  }
}

/**
 * Logger class for structured logging
 * Matches the API's StdLogger pattern from apps/api/internal/observability/std_logger.go
 */
export class Logger {
  private service: string;
  private component?: string;

  constructor(service: string, component?: string) {
    this.service = service;
    this.component = component;
  }

  /**
   * Convert object fields to Field array
   */
  private objectToFields(obj: Record<string, unknown>): Field[] {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  /**
   * Log at DEBUG level
   */
  debug(message: string, fields?: Record<string, unknown>): void {
    if (!shouldLog("debug")) return;
    const entry = createLogEntry(
      this.service,
      this.component,
      "debug",
      message,
      fields ? this.objectToFields(fields) : [],
    );
    outputLog(entry);
  }

  /**
   * Log at INFO level
   */
  info(message: string, fields?: Record<string, unknown>): void {
    if (!shouldLog("info")) return;
    const entry = createLogEntry(
      this.service,
      this.component,
      "info",
      message,
      fields ? this.objectToFields(fields) : [],
    );
    outputLog(entry);
  }

  /**
   * Log at WARN level
   */
  warn(message: string, fields?: Record<string, unknown>): void {
    if (!shouldLog("warn")) return;
    const entry = createLogEntry(
      this.service,
      this.component,
      "warn",
      message,
      fields ? this.objectToFields(fields) : [],
    );
    outputLog(entry);
  }

  /**
   * Log at ERROR level
   */
  error(
    message: string,
    error?: Error,
    fields?: Record<string, unknown>,
  ): void {
    if (!shouldLog("error")) return;

    const allFields: Field[] = [];
    if (error) {
      allFields.push({
        key: "error",
        value: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
      });
    }
    if (fields) {
      allFields.push(...this.objectToFields(fields));
    }

    const entry = createLogEntry(
      this.service,
      this.component,
      "error",
      message,
      allFields,
    );
    outputLog(entry);
  }

  /**
   * Log at FATAL level
   */
  fatal(message: string, fields?: Record<string, unknown>): void {
    if (!shouldLog("fatal")) return;
    const entry = createLogEntry(
      this.service,
      this.component,
      "fatal",
      message,
      fields ? this.objectToFields(fields) : [],
    );
    outputLog(entry);
  }

  /**
   * Convenience method for error logging with method context
   * Matches API's LogError pattern
   */
  logError(method: string, err: Error, fields?: Record<string, unknown>): void {
    const allFields: Record<string, unknown> = {
      method,
      ...(fields || {}),
    };

    this.error(`Operation failed: ${method}`, err, allFields);
  }

  /**
   * Log application startup with version info
   */
  logStartup(): void {
    const buildInfo = getBuildInfo();
    const env = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV;

    this.info("Application starting", {
      version: buildInfo.version,
      commit: buildInfo.commit,
      build_time: buildInfo.buildTime,
      environment: env,
    });
  }

  /**
   * Create a child logger with a different service name
   */
  withService(service: string): Logger {
    return new Logger(service, this.component);
  }

  /**
   * Create a child logger with a different component name
   */
  withComponent(component: string): Logger {
    return new Logger(this.service, component);
  }
}

/**
 * Create a new logger instance
 * @param service - Service name (e.g., 'web', 'web-app')
 * @param component - Component name (e.g., 'auth', 'events', 'profile')
 */
export function createLogger(service: string, component?: string): Logger {
  return new Logger(service, component);
}

/**
 * Default logger instance for the web application
 */
export const logger = new Logger("web");

/**
 * Helper to create fields for logging
 */
export function field(key: string, value: unknown): Field {
  return { key, value };
}

/**
 * Helper to create method field
 */
export function methodField(method: string): Field {
  return field("method", method);
}

/**
 * Helper to create error field
 */
export function errorField(err: Error): Field {
  return field("error", {
    name: err.name,
    message: err.message,
    stack: err.stack,
  });
}

/**
 * Helper to create user ID field
 */
export function userIDField(userID: string): Field {
  return field("user_id", userID);
}

/**
 * Helper to create correlation ID field
 */
export function correlationIDField(correlationID: string): Field {
  return field("correlation_id", correlationID);
}

/**
 * Helper to create trace ID field (formats for GCP trace correlation)
 */
export function traceIDField(traceID: string): Field {
  return field("trace_id", traceID);
}

/**
 * Helper to create span ID field (for distributed tracing)
 */
export function spanIDField(spanID: string): Field {
  return field("span_id", spanID);
}

/**
 * Helper to create HTTP request field
 * Automatically recognized by Cloud Logging for request analysis
 */
export function httpRequestField(request: Request): Field {
  const httpRequest: HttpRequestInfo = {
    requestMethod: request.method,
    requestUrl: request.url,
    userAgent: request.headers.get("user-agent") || undefined,
    referer: request.headers.get("referer") || undefined,
    protocol: new URL(request.url).protocol.replace(":", ""),
  };

  return field("httpRequest", httpRequest);
}

/**
 * Helper to create HTTP request field from Next.js request context
 */
export function httpRequestFromContext(
  url: string,
  method: string,
  status?: number,
  userAgent?: string,
): Field {
  const httpRequest: HttpRequestInfo = {
    requestMethod: method,
    requestUrl: url,
    status,
    userAgent,
  };

  return field("httpRequest", httpRequest);
}
